import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Button, TextField } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useAudioRecognition } from 'AudioRecognition';
import { useMidiContext } from 'Contexts/MidiContext';
import {
  buildAudioCalibrationDiagnosticsMetadata,
  buildAudioDiagnosticsSession,
  buildAudioDiagnosticsScreenshotSvg,
  copyTextWithFallback,
  downloadBlob,
  formatAudioDiagnosticsClipboard,
  formatDiagnosticNotes,
  formatDiagnosticNumber,
  formatDiagnosticPercent,
  parseRecognitionExpectedNotes,
  svgToPngBlob,
  type AudioDiagnosticsFrame,
  type AudioDiagnosticsSnapshot,
} from '../utils/audioDiagnostics';

const sheetHeaderSecondaryButtonStyles = (darkMode: boolean) => ({
  color: darkMode ? '#EAF2FF' : '#10203A',
  borderColor: darkMode ? 'rgba(243, 247, 255, 0.18)' : 'rgba(16, 32, 58, 0.18)',
  borderRadius: '999px',
  minHeight: '28px',
  paddingX: '10px',
  textTransform: 'none',
  fontSize: '11px',
  fontWeight: 800,
  '&:hover': {
    borderColor: darkMode ? 'rgba(121, 216, 255, 0.38)' : 'rgba(111, 135, 241, 0.36)',
    backgroundColor: 'rgba(111, 135, 241, 0.08)',
  },
  '&.Mui-disabled': {
    color: darkMode ? 'rgba(226, 234, 255, 0.34)' : 'rgba(16, 32, 58, 0.36)',
    borderColor: darkMode ? 'rgba(243, 247, 255, 0.08)' : 'rgba(16, 32, 58, 0.1)',
  },
});

const audioDiagnosticsListStyles = (darkMode: boolean, height = 76) => ({
  borderRadius: '8px',
  border: darkMode ? '1px solid rgba(243,247,255,0.12)' : '1px solid rgba(17,24,39,0.1)',
  backgroundColor: darkMode ? 'rgba(3, 8, 16, 0.64)' : 'rgba(255,255,255,0.42)',
  padding: '6px 8px',
  height,
  minWidth: 0,
  overflow: 'hidden',
});

const audioDiagnosticsListTitleStyles = (darkMode: boolean) => ({
  color: darkMode ? '#F3F7FF' : '#111827',
  fontSize: '10px',
  fontWeight: 900,
  marginBottom: '4px',
});

const audioDiagnosticsRowStyles = (darkMode: boolean) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(44px, 0.75fr) minmax(62px, 1fr) minmax(76px, 1fr)',
  gap: '5px',
  alignItems: 'center',
  color: darkMode ? 'rgba(243,247,255,0.82)' : 'rgba(17,24,39,0.76)',
  fontSize: '9.5px',
  minHeight: '16px',
  lineHeight: 1.15,
  whiteSpace: 'nowrap',
});

type AudioDiagnosticsPanelProps = {
  snapshot: AudioDiagnosticsSnapshot | null;
  frames: AudioDiagnosticsFrame[];
  darkMode: boolean;
};

const diagnosticsTextFieldStyles = (darkMode: boolean) => ({
  '& .MuiInputLabel-root': {
    color: darkMode ? 'rgba(243, 247, 255, 0.68)' : 'rgba(17, 24, 39, 0.58)',
    fontWeight: 700,
    fontSize: '11px',
  },
  '& .MuiInputBase-root': {
    color: darkMode ? '#F3F7FF' : '#111827',
    borderRadius: '8px',
    backgroundColor: darkMode ? 'rgba(3, 8, 16, 0.58)' : 'rgba(255, 255, 255, 0.46)',
    fontSize: '11px',
    minHeight: '34px',
  },
  '& .MuiInputBase-input': {
    padding: '7px 10px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: darkMode ? 'rgba(243,247,255,0.14)' : 'rgba(17,24,39,0.14)',
  },
  '& .MuiFormHelperText-root': {
    color: '#D97706',
    fontWeight: 700,
    fontSize: '9px',
    marginTop: '2px',
  },
});

const AudioDiagnosticsPanel = ({ snapshot, frames, darkMode }: AudioDiagnosticsPanelProps) => {
  const { calibrationProfile } = useAudioRecognition();
  const { audioInputLatencyMs } = useMidiContext();
  const [copyStatus, setCopyStatus] = useState('');
  const [screenshotStatus, setScreenshotStatus] = useState('');
  const [exportStatus, setExportStatus] = useState('');
  const [sessionLabel, setSessionLabel] = useState('');
  const [expectedNotesInput, setExpectedNotesInput] = useState('');
  const peaks = snapshot?.peaks ?? [];
  const maxAmp = Math.max(...peaks.map((peak) => peak.amp), 0.000001);
  const chartWidth = 680;
  const chartHeight = 136;
  const chartPaddingX = 28;
  const chartPaddingBottom = 34;
  const chartPaddingTop = 12;
  const barAreaHeight = chartHeight - chartPaddingTop - chartPaddingBottom;
  const barGap = 6;
  const barWidth =
    peaks.length > 0
      ? (chartWidth - chartPaddingX * 2 - barGap * Math.max(0, peaks.length - 1)) / peaks.length
      : 0;
  const panelTextColor = darkMode ? '#F3F7FF' : '#111827';
  const secondaryTextColor = darkMode ? 'rgba(243, 247, 255, 0.68)' : 'rgba(17, 24, 39, 0.64)';
  const parsedExpectedNotes = useMemo(
    () => parseRecognitionExpectedNotes(expectedNotesInput),
    [expectedNotesInput],
  );

  const clearStatusLater = useCallback((setter: (value: string) => void) => {
    window.setTimeout(() => setter(''), 1800);
  }, []);

  const handleCopyDiagnostics = useCallback(async () => {
    if (!snapshot) return;
    try {
      await copyTextWithFallback(formatAudioDiagnosticsClipboard(snapshot));
      setCopyStatus('Copied');
      clearStatusLater(setCopyStatus);
    } catch (error) {
      setCopyStatus(error instanceof Error ? error.message : 'Copy failed');
      clearStatusLater(setCopyStatus);
    }
  }, [clearStatusLater, snapshot]);

  const handleScreenshotDiagnostics = useCallback(async () => {
    if (!snapshot) return;
    try {
      const pngBlob = await svgToPngBlob(buildAudioDiagnosticsScreenshotSvg(snapshot));
      downloadBlob(
        pngBlob,
        `audio-diagnostics-${snapshot.capturedAtIso.replace(/[:.]/g, '-')}.png`,
      );
      setScreenshotStatus('Saved');
      clearStatusLater(setScreenshotStatus);
    } catch (error) {
      setScreenshotStatus(error instanceof Error ? error.message : 'Screenshot failed');
      clearStatusLater(setScreenshotStatus);
    }
  }, [clearStatusLater, snapshot]);

  const handleExportSession = useCallback(() => {
    if (!snapshot || frames.length === 0) return;
    if (parsedExpectedNotes.invalidTokens.length > 0) {
      setExportStatus(`Invalid: ${parsedExpectedNotes.invalidTokens.join(', ')}`);
      clearStatusLater(setExportStatus);
      return;
    }

    const session = buildAudioDiagnosticsSession(frames, {
      label: sessionLabel,
      expectedMidiNotes: parsedExpectedNotes.midiNotes,
      metadata: {
        userAgent: window.navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        ...buildAudioCalibrationDiagnosticsMetadata(calibrationProfile, audioInputLatencyMs),
      },
    });
    if (!session) return;

    const blob = new Blob([JSON.stringify(session, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    downloadBlob(
      blob,
      `audio-recognition-session-${session.createdAtIso.replace(/[:.]/g, '-')}.json`,
    );

    const captureScore = session.evaluations?.capture.observedF1;
    setExportStatus(
      captureScore == null
        ? 'Exported'
        : `Exported · capture decision F1 ${formatDiagnosticPercent(captureScore)}`,
    );
    clearStatusLater(setExportStatus);
  }, [
    audioInputLatencyMs,
    calibrationProfile,
    clearStatusLater,
    frames,
    parsedExpectedNotes,
    sessionLabel,
    snapshot,
  ]);

  return (
    <Box
      sx={{
        borderBottom: darkMode
          ? '1px solid rgba(243, 247, 255, 0.12)'
          : '1px solid rgba(9, 19, 36, 0.08)',
        backgroundColor: darkMode ? 'rgba(8, 17, 30, 0.92)' : 'rgba(247, 232, 205, 0.72)',
        padding: { xs: '8px 10px', md: '8px 14px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '6px',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ color: panelTextColor, fontSize: '12px', fontWeight: 900 }}>
            Audio diagnostics
          </Box>
          <Box sx={{ color: secondaryTextColor, fontSize: '10px', marginTop: '2px' }}>
            {snapshot
              ? `RMS ${formatDiagnosticNumber(snapshot.rms, 6)} · stable ${formatDiagnosticNotes(
                  snapshot.midiNotes,
                )} · onsets ${formatDiagnosticNotes(snapshot.onsetMidiNotes)} · mode ${
                  snapshot.recognitionMode
                } · residual ${
                  snapshot.residualEnergy == null
                    ? 'n/a'
                    : formatDiagnosticNumber(snapshot.residualEnergy, 3)
                }`
              : 'Waiting for audio frames'}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyDiagnostics}
            disabled={!snapshot}
            sx={sheetHeaderSecondaryButtonStyles(darkMode)}
          >
            Copy data
          </Button>
          <Button
            variant="outlined"
            startIcon={<CameraAltIcon />}
            onClick={handleScreenshotDiagnostics}
            disabled={!snapshot}
            sx={sheetHeaderSecondaryButtonStyles(darkMode)}
          >
            Screenshot
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportSession}
            disabled={!snapshot || frames.length === 0}
            sx={sheetHeaderSecondaryButtonStyles(darkMode)}
          >
            Export session
          </Button>
          {(copyStatus || screenshotStatus || exportStatus) && (
            <Box sx={{ color: secondaryTextColor, fontSize: '10px', fontWeight: 700 }}>
              {copyStatus || screenshotStatus || exportStatus}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: 'minmax(160px, 0.7fr) minmax(220px, 1fr)',
          },
          gap: '8px',
          marginBottom: '6px',
        }}
      >
        <TextField
          size="small"
          label="Session label"
          value={sessionLabel}
          onChange={(event) => setSessionLabel(event.target.value)}
          placeholder="Middle C"
          sx={diagnosticsTextFieldStyles(darkMode)}
        />
        <TextField
          size="small"
          label="Expected notes"
          value={expectedNotesInput}
          onChange={(event) => setExpectedNotesInput(event.target.value)}
          placeholder="C4 E4 G4"
          error={parsedExpectedNotes.invalidTokens.length > 0}
          helperText={
            parsedExpectedNotes.invalidTokens.length > 0
              ? `Invalid: ${parsedExpectedNotes.invalidTokens.join(', ')}`
              : ' '
          }
          sx={diagnosticsTextFieldStyles(darkMode)}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: 'minmax(0, 1.45fr) minmax(260px, 0.55fr)',
          },
          gap: '8px',
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            overflowX: 'auto',
            borderRadius: '8px',
            border: darkMode ? '1px solid rgba(243,247,255,0.12)' : '1px solid rgba(17,24,39,0.1)',
            backgroundColor: darkMode ? 'rgba(3, 8, 16, 0.64)' : 'rgba(255,255,255,0.42)',
            height: 144,
            overflowY: 'hidden',
          }}
        >
          <svg
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            width="100%"
            height="144"
            role="img"
            aria-label="Audio frequency peak amplitudes"
            style={{ minWidth: 520, display: 'block' }}
          >
            <line
              x1={chartPaddingX}
              y1={chartHeight - chartPaddingBottom}
              x2={chartWidth - chartPaddingX}
              y2={chartHeight - chartPaddingBottom}
              stroke={darkMode ? 'rgba(243,247,255,0.24)' : 'rgba(17,24,39,0.24)'}
            />
            {peaks.length === 0 && (
              <text
                x={chartWidth / 2}
                y={chartHeight / 2}
                textAnchor="middle"
                fill={secondaryTextColor}
                fontSize="11"
                fontWeight="700"
              >
                No peaks yet
              </text>
            )}
            {peaks.map((peak, index) => {
              const heightRatio = Math.max(0.02, Math.min(1, peak.amp / maxAmp));
              const barHeight = barAreaHeight * heightRatio;
              const x = chartPaddingX + index * (barWidth + barGap);
              const y = chartHeight - chartPaddingBottom - barHeight;

              return (
                <g key={`${peak.note}-${peak.hz}-${index}`}>
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    rx="4"
                    fill={index === 0 ? '#79D8FF' : '#6F87F1'}
                  />
                  <text
                    x={x + barWidth / 2}
                    y={chartHeight - 25}
                    textAnchor="middle"
                    fill={panelTextColor}
                    fontSize="10"
                    fontWeight="800"
                  >
                    {peak.note}
                  </text>
                  <text
                    x={x + barWidth / 2}
                    y={chartHeight - 9}
                    textAnchor="middle"
                    fill={secondaryTextColor}
                    fontSize="8.5"
                  >
                    {formatDiagnosticNumber(peak.hz, 1)}
                  </text>
                </g>
              );
            })}
          </svg>
        </Box>

        <Box sx={{ display: 'grid', gap: '6px', minWidth: 0 }}>
          <Box sx={audioDiagnosticsListStyles(darkMode, 94)}>
            <Box sx={audioDiagnosticsListTitleStyles(darkMode)}>Rolling top peaks</Box>
            {(snapshot?.peakSummary ?? []).slice(0, 4).map((peak) => (
              <Box key={`${peak.midi}-${peak.hz}`} sx={audioDiagnosticsRowStyles(darkMode)}>
                <Box sx={{ fontWeight: 900 }}>{peak.note}</Box>
                <Box>{formatDiagnosticNumber(peak.hz, 1)} Hz</Box>
                <Box>max {formatDiagnosticNumber(peak.maxAmp, 5)}</Box>
              </Box>
            ))}
            {(!snapshot || snapshot.peakSummary.length === 0) && (
              <Box sx={{ color: secondaryTextColor, fontSize: '9.5px' }}>No peak summary yet</Box>
            )}
          </Box>
          <Box sx={audioDiagnosticsListStyles(darkMode, 62)}>
            <Box sx={audioDiagnosticsListTitleStyles(darkMode)}>Sparse estimate</Box>
            {(snapshot?.selectedNotes ?? []).slice(0, 2).map((note) => (
              <Box key={note.midi} sx={audioDiagnosticsRowStyles(darkMode)}>
                <Box sx={{ fontWeight: 900 }}>{note.note}</Box>
                <Box>midi {note.midi}</Box>
                <Box>norm {formatDiagnosticNumber(note.normalizedEnergy, 3)}</Box>
              </Box>
            ))}
            {(!snapshot || snapshot.selectedNotes.length === 0) && (
              <Box sx={{ color: secondaryTextColor, fontSize: '9.5px' }}>No selected notes</Box>
            )}
          </Box>
          <Box sx={audioDiagnosticsListStyles(darkMode, 62)}>
            <Box sx={audioDiagnosticsListTitleStyles(darkMode)}>Suppressed</Box>
            {(snapshot?.suppressedNotes ?? []).slice(0, 2).map((note) => (
              <Box key={`${note.midi}-${note.reason}`} sx={audioDiagnosticsRowStyles(darkMode)}>
                <Box sx={{ fontWeight: 900 }}>{note.note}</Box>
                <Box>{note.reason}</Box>
                <Box>{note.suppressedByNote ? `by ${note.suppressedByNote}` : 'low conf'}</Box>
              </Box>
            ))}
            {(!snapshot || snapshot.suppressedNotes.length === 0) && (
              <Box sx={{ color: secondaryTextColor, fontSize: '9.5px' }}>No suppressed notes</Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: '6px',
            }}
          >
            <Box sx={audioDiagnosticsListStyles(darkMode, 74)}>
              <Box sx={audioDiagnosticsListTitleStyles(darkMode)}>Create candidates</Box>
              {(snapshot?.captureCandidates ?? []).slice(0, 3).map((candidate) => (
                <Box key={candidate.midi} sx={audioDiagnosticsRowStyles(darkMode)}>
                  <Box sx={{ fontWeight: 900 }}>{candidate.note}</Box>
                  <Box>midi {candidate.midi}</Box>
                  <Box>score {formatDiagnosticNumber(candidate.captureScore, 3)}</Box>
                </Box>
              ))}
              {(!snapshot || snapshot.captureCandidates.length === 0) && (
                <Box sx={{ color: secondaryTextColor, fontSize: '9.5px' }}>
                  No capture candidates
                </Box>
              )}
            </Box>
            <Box sx={audioDiagnosticsListStyles(darkMode, 74)}>
              <Box sx={audioDiagnosticsListTitleStyles(darkMode)}>Last captured onset</Box>
              {(snapshot?.lastCaptureCandidates ?? []).slice(0, 3).map((candidate) => (
                <Box key={candidate.midi} sx={audioDiagnosticsRowStyles(darkMode)}>
                  <Box sx={{ fontWeight: 900 }}>{candidate.note}</Box>
                  <Box>midi {candidate.midi}</Box>
                  <Box>score {formatDiagnosticNumber(candidate.captureScore, 3)}</Box>
                </Box>
              ))}
              {(!snapshot || snapshot.lastCaptureCandidates.length === 0) && (
                <Box sx={{ color: secondaryTextColor, fontSize: '9.5px' }}>No onset captured</Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AudioDiagnosticsPanel;
